import { Component, Input, SimpleChanges, OnChanges } from '@angular/core';
import { LedgersService } from 'src/app/share/services/ledgers/ledgers.service';
import { DefaultConfig } from 'src/app/share/configs/plotly.config';

@Component({
    selector: 'app-performance-sale-value',
    templateUrl: './performance-sale-value.component.html',
    styleUrls: ['./performance-sale-value.component.scss'],
    standalone: false
})
export class PerformanceSaleValueComponent implements OnChanges {
  @Input() selectedRange: any;
  @Input() selectedTyre: any;

  loading: boolean = false;
  errorMessage: string = '';

  public graph: any = {
    data: [],
    layout: {},
    config: {}
  };

  tabledata: any[] = [];
  viewMode: 'chart' | 'table' = 'chart';


  constructor(private readonly ledgersService: LedgersService) { }

  setViewMode(mode: 'chart' | 'table') {
    this.viewMode = mode;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedRange']?.currentValue) {
      this.getData();
    }
  }

  getData() {
    if (!this.selectedTyre || !this.selectedRange) return;

    this.loading = true;
    this.errorMessage = '';

    const payload = {
      tyreSize: this.selectedTyre.tyreSize,
      tyreMake: this.selectedTyre.tyreMake,
      tyreModel: this.selectedTyre.tyreModel,
      created_datefrom: this.selectedRange.startDate,
      created_dateupto: this.selectedRange.endDate,
    };

    this.ledgersService.getSaveValueByBranch(payload).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res?.data) {
          this.mapApiData(res.data);
        } else {
          this.errorMessage = res?.message || 'No data found';
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.errorMessage = error?.error?.message || "Failed to fetch sale metrics";
      }
    });
  }


  mapApiData(apiData: any) {
    const companies = apiData?.map((item: any) => item.branchName || '') || [];
    const volume = apiData?.map((item: any) => item.totalQuantity || 0) || [];
    const sale = apiData?.map((item: any) => item.totalSalesValue || 0) || [];
    this.initGraph({ companies, volume, sale });
    this.mapTabledata({ companies, volume, sale });
  }

  mapTabledata(apiData: any) {
    const companies = apiData.companies || [];
    const volumes = apiData.volume || [];
    const sales = apiData.sale || [];

    this.tabledata = companies.map((company: string, index: number) => {
      return {
        company: company,
        saleVolume: volumes[index] || 0,
        saleValue: sales[index] || 0
      };
    });
  }

  initGraph(apiData: any) {
    // apiData structure: { volume: number[], sale: number[], companies: string[] }
    const labels = (apiData.companies || []).map((label: string, index: number) => {
      return label + '\u200b'.repeat(index);
    });
    const volumeData = apiData.volume || [];
    const saleData = apiData.sale || [];
    const maxAbsVolume = volumeData.length > 0 ? Math.max(...volumeData.map((v: any) => Math.abs(v))) : 0;

    this.graph.data = [
      {
        x: labels,
        y: saleData,
        name: 'Sale value (₹)',
        type: 'scatter',
        mode: 'lines+markers',
        yaxis: 'y2',
        line: { color: '#059669', width: 2 },
        marker: { size: 8, color: '#059669' },
        hovertemplate: '<b>%{x}</b><br>Sale Value: ₹%{y:,.0f}<extra></extra>',
      },
      {
        x: labels,
        y: volumeData,
        name: 'Volume (units)',
        type: 'bar',
        marker: {
          color: '#00305C',
          cornerradius: 12
        },
        hovertemplate: '<b>%{x}</b><br>Volume: %{y}<extra></extra>',
      }
    ];

    let gap = 0.2;
    if (labels.length <= 1) {
      gap = 0.8;
    } else if (labels.length <= 3) {
      gap = 0.6;
    }

    this.graph.layout = {
      autosize: true,
      xaxis: {
        type: 'category',
        showgrid: false,
        zeroline: false,
        tickfont: { color: '#00305C', size: 10, family: 'Arial, sans-serif' },
        automargin: true,
        tickangle: labels.length > 6 ? -45 : 0,
        fixedrange: true,
        domain: [0.22, 0.95] // Leave more space on the left for two scales
      },
      yaxis: {
        showgrid: true,
        gridcolor: '#f1f5f9',
        zeroline: true,
        zerolinecolor: '#e2e8f0',
        tickfont: { color: '#64748b', size: 10 },
        tickformat: 'd',
        dtick: maxAbsVolume < 6 ? 1 : undefined,
        rangemode: 'tozero',
        fixedrange: true,
        side: 'left',
        anchor: 'free',
        position: 0 // First scale: Volume (Gray)
      },
      yaxis2: {
        overlaying: 'y',
        side: 'left',
        anchor: 'free',
        position: 0.11, // Second scale: Sale Value (Green)
        showgrid: false,
        zeroline: false,
        tickfont: { color: '#059669', size: 10 },
        rangemode: 'tozero',
        fixedrange: true,
      },
      margin: { l: 35, r: 20, t: 40, b: 120 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      hovermode: 'closest',
      bargap: gap,
      showlegend: true,
      legend: {
        orientation: 'h',
        x: 0,
        xanchor: 'left',
        y: 1.15,
        font: { size: 10, color: '#64748b' },
        bgcolor: 'rgba(0,0,0,0)',
      }
    };

    this.graph.config = {
      ...DefaultConfig,
    };
  }
}
